<template>
  <AisPagination :padding="4">
    <template
      #default="{
        createURL,
        currentRefinement,
        isFirstPage,
        isLastPage,
        nbHits,
        nbPages,
        pages,
        refine,
      }: AisSearchPagination"
    >
      <!-- If there's no content in the slot, the default UI will appear. This hides the default UI when there are no hits. -->
      <div v-if="nbHits === 0" />

      <nav
        v-else
        aria-label="Search Results Pagination"
        class="flex justify-between items-center gap-x-4 border-t border-gray-300"
      >
        <p
          class="max-lg:hidden"
          v-text="`Showing ${currentRefinement * HITS_PER_PAGE + 1} to ${Math.min((currentRefinement + 1) * HITS_PER_PAGE, nbHits)} of ${nbHits} results`"
        />

        <div class="flex justify-between items-center gap-x-4 lg:hidden w-full">
          <a
            :href="createURL(currentRefinement - 1)"
            class="wc-focus py-3 focus-visible:-outline-offset-1"
            @click.prevent="refine(currentRefinement - 1)"
          >
            <span v-text="'Previous'" />
            <span
              class="sr-only"
              v-text="' Page'"
            />
          </a>
          <a
            :href="createURL(currentRefinement + 1)"
            class="wc-focus py-3 focus-visible:-outline-offset-1"
            @click.prevent="refine(currentRefinement + 1)"
          >
            <span v-text="'Next'" />
            <span
              class="sr-only"
              v-text="' Page'"
            />
          </a>
        </div>

        <ul class="max-lg:hidden flex items-center body-md -mt-px">
          <li v-if="!isFirstPage">
            <a
              :href="createURL(0)"
              class="wc-focus py-3 h-12 w-10 flex items-center justify-center hover:text-primary-500 focus-visible:-outline-offset-1"
              @click.prevent="refine(0)"
            >
              <IconArrowNarrowLeft :aria-hidden="true" />
              <span
                class="sr-only"
                v-text="'First Page'"
              />
            </a>
          </li>

          <li
            v-for="page in pages"
            :key="`search-page-${page}`"
          >
            <a
              :class="{ 'text-primary-500 border-t-2 border-primary-500': page === currentRefinement }"
              :href="createURL(page)"
              class="wc-focus py-3 h-12 w-10 flex items-center justify-center hover:text-primary-500 focus-visible:-outline-offset-1"
              @click.prevent="refine(page)"
            >
              <span
                class="sr-only"
                v-text="'Page '"
              />
              <span v-text="page + 1" />
            </a>
          </li>

          <li v-if="!isLastPage && nbPages > PAGES_TO_SHOW">
            <span
              class="py-3 h-12 w-10 flex items-center justify-center"
              v-text="'&middot;&middot;&middot;'"
            />
          </li>

          <li v-if="!isLastPage && nbPages > PAGES_TO_SHOW">
            <a
              :class="{ 'text-primary-500 border-t-2 border-primary-500': nbPages === currentRefinement }"
              :href="createURL(nbPages)"
              class="wc-focus py-3 h-12 w-10 flex items-center justify-center hover:text-primary-500 focus-visible:-outline-offset-1"
              @click.prevent="refine(nbPages)"
              v-text="nbPages"
            />
          </li>

          <li v-if="!isLastPage">
            <a
              :href="createURL(nbPages)"
              class="wc-focus py-3 h-12 w-10 flex items-center justify-center hover:text-primary-500 focus-visible:-outline-offset-1"
              @click.prevent="refine(nbPages)"
            >
              <IconArrowNarrowRight :aria-hidden="true" />
              <span
                class="sr-only"
                v-text="'Last Page'"
              />
            </a>
          </li>
        </ul>
      </nav>
    </template>
  </AisPagination>
</template>

<script lang="ts" setup>
import { AisPagination } from 'vue-instantsearch/vue3/es'
import { IconArrowNarrowLeft, IconArrowNarrowRight } from '@tabler/icons-vue'

import { AisSearchPagination } from '@js/types/search/AisSearchPagination'

defineOptions({
  name: 'BaseSearchPagination',
})

const PAGES_TO_SHOW = 5
const HITS_PER_PAGE = 10
</script>
